import type { ReactElement } from "react";
import { Button, Modal } from "@playbookai/ui";

/** The "Learn more" dialog: what adoption measures, how the comparison works,
    what happens when a module's availability changes, and how to read the
    chart. Copy only. */
export function AdoptionLearnMore({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Understanding adoption"
      description="How activity, comparisons and module availability are represented."
      describedBy="adoption-help"
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div
        id="adoption-help"
        className="space-y-4 text-sm text-pb-text-secondary"
      >
        <section>
          <h3 className="font-semibold text-pb-text">What is measured</h3>
          <p className="mt-1">
            SOLVE, VSM and Hoshin count new work on its creation date. LAMA
            counts an assessment wave when its score snapshot is completed.
            Archived work remains part of its original period.
          </p>
        </section>
        <section>
          <h3 className="font-semibold text-pb-text">Reading the comparison</h3>
          <p className="mt-1">
            Each total is compared with the immediately preceding period of the
            same length. A new-activity message replaces a percentage when the
            earlier period was zero.
          </p>
        </section>
        <section>
          <h3 className="font-semibold text-pb-text">
            When module availability changes
          </h3>
          <p className="mt-1">
            Turning a module off does not erase its history. Previously used
            modules remain visible as Disabled now with a dashed trend. A module
            that was never enabled shows an em dash and no trend line. When it
            is enabled again, new activity resumes on the same timeline.
          </p>
        </section>
        <section>
          <h3 className="font-semibold text-pb-text">Reading the chart</h3>
          <p className="mt-1">
            Dots mark exact non-zero daily counts in UTC. The smooth line
            connects those daily values without introducing negative counts or
            exaggerated peaks. Inactive days remain on the neutral zero
            baseline.
          </p>
        </section>
      </div>
    </Modal>
  );
}
